import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/config';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, MapPin, Send, MousePointer, Info } from 'lucide-react';
import { toast } from 'sonner';
import { getErrorMessage } from '@/utils/errorHandler';
import ImageUploader from '@/components/ImageUploader';
import SpamProtection from '@/components/SpamProtection';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

import { categories } from '@/constants/categories';

// Fix Leaflet marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom marker icon
const createLocationIcon = () => {
  return L.divIcon({
    html: `<div style="background-color: #f59e0b; width: 32px; height: 32px; border-radius: 50% 50% 50% 0; transform: rotate(-45deg); border: 3px solid white; box-shadow: 0 4px 12px rgba(0,0,0,0.3); display: flex; align-items: center; justify-content: center;"><div style="transform: rotate(45deg); color: white; font-weight: bold; font-size: 16px;">📍</div></div>`,
    className: '',
    iconSize: [32, 32],
    iconAnchor: [16, 32],
  });
};

// Component to handle map clicks
const LocationPicker = ({ position, onLocationSelect }) => {
  const map = useMap();
  
  useMapEvents({
    click: (e) => {
      onLocationSelect(e.latlng.lat, e.latlng.lng);
    },
  });
  
  useEffect(() => {
    if (position[0] !== -26.6500 || position[1] !== 153.0667) {
      map.flyTo(position, 16, { duration: 0.5 });
    }
  }, [position, map]);
  
  return position[0] && position[1] ? (
    <Marker position={position} icon={createLocationIcon()} />
  ) : null;
};

const PublicSubmitBox = () => {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [geocoding, setGeocoding] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    category: 'homegrown',
    description: '',
    address: '',
    lat: -26.6500,
    lng: 153.0667,
    hours: '',
    contact_info: '',
    images: [],
    is_active: false,
    website_url: '', // Honeypot
  });
  const [isVerified, setIsVerified] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const [showMapPicker, setShowMapPicker] = useState(false);
  const [locationManuallySet, setLocationManuallySet] = useState(false);

  const geocodeAddress = async () => {
    if (!formData.address || formData.address.trim().length < 5) {
      toast.error('Please enter a valid address');
      return;
    }

    setGeocoding(true);

    try {
      // Use Google Maps Geocoding via backend API for precise street-level accuracy
      const response = await axios.post(`${API}/geocode`, {
        address: formData.address,
        region: 'AU'
      });

      if (response.data.success) {
        const { latitude, longitude, location_type, street_number } = response.data;
        
        // Update coordinates only, keep original address
        setFormData(prev => ({
          ...prev,
          lat: latitude,
          lng: longitude,
        }));
        setShowMapPicker(true);
        setLocationManuallySet(false);
        
        // Show appropriate message based on precision
        if (location_type === 'ROOFTOP') {
          toast.success('Exact location found! You can adjust if needed.');
        } else if (street_number) {
          toast.success('Location found! Click on the map to fine-tune if needed.');
        } else {
          toast.success('Street found! Please click on the map to set the exact position.');
        }
      } else {
        toast.error(response.data.error || 'Address not found. Please check and try again.');
        // Still show map for manual placement
        setShowMapPicker(true);
      }
    } catch (error) {
      console.error('Geocoding error:', error);
      const message = error.response?.data?.detail || 'Failed to find location. You can set it manually on the map.';
      toast.error(message);
      // Still show the map so user can set location manually
      setShowMapPicker(true);
    } finally {
      setGeocoding(false);
    }
  };

  const handleLocationSelect = (lat, lng) => {
    setFormData(prev => ({
      ...prev,
      lat: lat,
      lng: lng,
    }));
    setLocationManuallySet(true);
    toast.success('Location set! The marker shows where the stall will appear on the map.');
  };

  const handleImagesChange = (newImages) => {
    setFormData(prev => ({ ...prev, images: newImages }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Client-side honeypot check
    const honeypot = document.getElementById('website_url')?.value;
    if (honeypot) {
      console.log("Spam detected");
      navigate('/');
      return;
    }

    if (!isVerified) {
      toast.error('Please complete the security check');
      return;
    }
    
    // Validate location is set
    if (formData.lat === -26.6500 && formData.lng === 153.0667) {
      toast.error('Please find the address and set the exact location on the map');
      return;
    }

    setSaving(true);

    try {
      // Include honeypot in payload for backend check
      await axios.post(`${API}/boxes/submit`, {
        ...formData,
        website_url: honeypot
      });
      toast.success('Box submitted successfully! It will appear on the map after admin approval.');
      navigate('/');
    } catch (error) {
      console.error('Error submitting box:', error);
      toast.error(getErrorMessage(error, 'Failed to submit box'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-3xl">
      <Button
        variant="ghost"
        onClick={() => navigate('/')}
        className="mb-6"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Map
      </Button>

      <Card>
        <CardHeader>
          <CardTitle>Submit a Community Box</CardTitle>
          <CardDescription>
            Know of a local honesty box? Submit it here to share with the community.
            Submissions are reviewed by admins before appearing on the map.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Name */}
            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Roadside Citrus Stall"
                required
              />
            </div>

            {/* Category */}
            <div className="space-y-2">
              <Label htmlFor="category">Category *</Label>
              <Select
                value={formData.category}
                onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}
              >
                <SelectTrigger id="category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categories.filter(c => c.value !== 'all').map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>
                      {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description">Description *</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="What's typically available? (e.g., seasonal limes, lemons, honey)"
                rows={4}
                required
              />
            </div>

            {/* Address with Geocoding */}
            <div className="space-y-2">
              <Label htmlFor="address">Address *</Label>
              <div className="flex gap-2">
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                  placeholder="Enter full address including street number"
                  required
                  className="flex-1"
                />
                <Button
                  type="button"
                  onClick={geocodeAddress}
                  disabled={geocoding}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <MapPin className="w-4 h-4 mr-2" />
                  {geocoding ? 'Finding...' : 'Find'}
                </Button>
              </div>
            </div>

            {/* Interactive Map for Location Fine-tuning */}
            {showMapPicker && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-2">
                    <MousePointer className="w-4 h-4" />
                    Set Exact Location *
                  </Label>
                  {locationManuallySet && (
                    <span className="text-sm text-green-600 flex items-center gap-1">
                      ✓ Location set
                    </span>
                  )}
                </div>
                
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-2">
                  <p className="text-sm text-amber-800 flex items-start gap-2">
                    <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>
                      <strong>Click on the map</strong> to set the exact location of this stall. 
                      This helps visitors find it accurately.
                    </span>
                  </p>
                </div>
                
                <div className="border-2 border-dashed border-amber-300 rounded-lg overflow-hidden" style={{ height: '250px' }}>
                  <MapContainer
                    center={[formData.lat, formData.lng]}
                    zoom={16}
                    style={{ height: '100%', width: '100%' }}
                  >
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <LocationPicker 
                      position={[formData.lat, formData.lng]} 
                      onLocationSelect={handleLocationSelect}
                    />
                  </MapContainer>
                </div>
                
                <p className="text-xs text-gray-500">
                  Coordinates: {formData.lat.toFixed(6)}, {formData.lng.toFixed(6)}
                </p>
              </div>
            )}

            {/* Show prompt if map not yet visible */}
            {!showMapPicker && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800 flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Enter the address above and click "Find" to show a map where you can set the exact location.
                </p>
              </div>
            )}

            {/* Hours */}
            <div className="space-y-2">
              <Label htmlFor="hours">Hours (Optional)</Label>
              <Input
                id="hours"
                value={formData.hours}
                onChange={(e) => setFormData(prev => ({ ...prev, hours: e.target.value }))}
                placeholder="e.g., Daylight hours"
              />
            </div>

            {/* Images */}
            <div className="space-y-2">
              <Label>Images (Optional)</Label>
              <ImageUploader 
                images={formData.images} 
                onImagesChange={handleImagesChange} 
                maxImages={3}
                onUploadStatusChange={setImageUploading}
              />
            </div>

            {/* Spam Protection */}
            <SpamProtection onVerify={setIsVerified} />

            {/* Submit */}
            <div className="flex gap-3 pt-4">
              <Button
                type="submit"
                className="flex-1 bg-green-600 hover:bg-green-700"
                disabled={saving || !isVerified || imageUploading}
              >
                <Send className="w-4 h-4 mr-2" />
                {saving ? 'Submitting...' : (imageUploading ? 'Uploading Image...' : 'Submit for Approval')}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/')}
              >
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default PublicSubmitBox;
